import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import {
  DocumentTextIcon,
  LinkIcon,
  NoSymbolIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/catalyst/button";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { InvoiceStatus } from "@/gql/graphql";
import { useNavigate } from "@tanstack/react-router";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  invoiceWorkOrderMutation,
  setWorkOrderInvoiceNotRequiredMutation,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersMutations";
import {
  createInvoiceCheckoutSessionMutation,
  createInvoicePdfMutation,
  issueInvoiceMutation,
} from "@/app/Invoices/GraphQL/invoiceMutations";
import { useTranslations } from "use-intl";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useInvoiceActions } from "@/app/Invoices/Hooks/useInvoiceActions";
import { CreditCardIcon, PaperAirplaneIcon } from "@heroicons/react/20/solid";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { PDFIcon } from "@/assets/Icons";
import { AssignInvoiceToWorkOrderDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AssignInvoiceToWorkOrderDialog";

const invoiceStatusColorMap: Record<InvoiceStatus, BadgeColor> = {
  [InvoiceStatus.Draft]: "zinc",
  [InvoiceStatus.Issued]: "yellow",
  [InvoiceStatus.Paid]: "green",
  [InvoiceStatus.Voided]: "zinc",
  [InvoiceStatus.Overdue]: "red",
};

export function WorkOrderInvoiceCard() {
  const workOrder = useWorkOrderRecord();
  const invoice = workOrder.currentInvoice;
  const navigate = useNavigate();
  const { isAdmin } = useTenant();
  const { mutateAsync: invoiceWorkOrder, isPending } = useGqlMutation(
    invoiceWorkOrderMutation,
  );
  const {
    mutateAsync: setInvoiceNotRequired,
    isPending: isSettingInvoiceNotRequired,
  } = useGqlMutation(setWorkOrderInvoiceNotRequiredMutation);
  const { mutateAsync: createCheckout, isPending: isCreatingCheckout } =
    useGqlMutation(createInvoiceCheckoutSessionMutation);
  const { mutateAsync: createPdf, isPending: isCreatingPdf } = useGqlMutation(
    createInvoicePdfMutation,
  );
  const { mutateAsync: issueInvoice, isPending: isIssuing } =
    useGqlMutation(issueInvoiceMutation);
  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const { open: openAssignInvoice } = useDialog(AssignInvoiceToWorkOrderDialog);
  const tStatus = useTranslations("invoice.status");
  const t = useTranslations("workOrder.cards.invoice");
  const getActions = useInvoiceActions();

  const handleAssignExisting = () => {
    openAssignInvoice({
      workOrderId: workOrder.id,
      supplierId: workOrder.supplier.id,
    });
  };

  const handlePreviewPdf = async () => {
    if (!invoice) return;
    const res = await createPdf({ id: invoice.id });
    openPdf({ url: res.createInvoicePdf });
  };

  const handlePayOnline = async () => {
    if (!invoice) return;
    const res = await createCheckout({ id: invoice.id });
    window.open(
      res.createInvoiceCheckoutSession.checkout_session_url,
      "_blank",
    );
  };

  const showPayButton =
    invoice &&
    [InvoiceStatus.Issued, InvoiceStatus.Overdue].includes(invoice.status);

  const showIssueButton =
    isAdmin && invoice && invoice.status === InvoiceStatus.Draft;

  const handleIssueInvoice = async () => {
    if (!invoice) return;
    await issueInvoice({ id: invoice.id });
  };

  const handleCreateInvoice = async () => {
    await invoiceWorkOrder({ id: workOrder.id });
  };

  const openDrawer = () => {
    if (!invoice) return;
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        invoiceId: invoice.id,
      }),
    });
  };

  if (!invoice && workOrder.invoice_not_required) {
    return (
      <Card className="border-2 border-dashed border-gray-300">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <div className="flex size-12 items-center justify-center rounded-full bg-gray-100">
            <NoSymbolIcon className="size-6 text-gray-400" />
          </div>
          <h3 className="mt-3 text-sm font-medium text-gray-900">
            {t("notRequired")}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            {t("notRequiredDescription")}
          </p>
          {isAdmin ? (
            <div className="mt-4">
              <Button
                outline
                isLoading={isSettingInvoiceNotRequired}
                onClick={() =>
                  setInvoiceNotRequired({
                    id: workOrder.id,
                    invoice_not_required: false,
                  })
                }
              >
                {t("requireInvoice")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (!invoice) {
    return (
      <Card className="border-2 border-dashed border-gray-300">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <div className="flex size-12 items-center justify-center rounded-full bg-gray-100">
            <DocumentTextIcon className="size-6 text-gray-400" />
          </div>
          <h3 className="mt-3 text-sm font-medium text-gray-900">
            {t("title")}
          </h3>
          <p className="mt-1 text-xs text-gray-500">{t("noInvoiceYet")}</p>
          {isAdmin ? (
            <div className="mt-4 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                outline
                onClick={handleCreateInvoice}
                disabled={isPending}
              >
                <PlusIcon className="h-4 w-4" />
                {isPending ? t("creating") : t("createInvoice")}
              </Button>
              <Button outline onClick={handleAssignExisting}>
                <LinkIcon className="h-4 w-4" />
                {t("assignExisting")}
              </Button>
              <Button
                outline
                isLoading={isSettingInvoiceNotRequired}
                onClick={() =>
                  setInvoiceNotRequired({
                    id: workOrder.id,
                    invoice_not_required: true,
                  })
                }
              >
                <NoSymbolIcon className="h-4 w-4" />
                {t("notRequired")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={
        isAdmin
          ? "cursor-pointer transition-shadow hover:shadow-md"
          : "transition-shadow"
      }
      onClick={isAdmin ? openDrawer : undefined}
    >
      <CardHeader className="border-b px-4 py-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-4 w-4 text-gray-500" />
            <span>{t("title")}</span>
            <Badge color={invoiceStatusColorMap[invoice.status]}>
              {tStatus(invoice.status)}
            </Badge>
          </div>
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {showIssueButton ? (
              <Button
                color="indigo"
                onClick={handleIssueInvoice}
                disabled={isIssuing}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {t("issue")}
              </Button>
            ) : null}
            <Button outline onClick={handlePreviewPdf} disabled={isCreatingPdf}>
              <PDFIcon className="h-4 w-4" />
              {t("previewPdf")}
            </Button>
            {showPayButton ? (
              <Button
                color="green"
                onClick={handlePayOnline}
                disabled={isCreatingCheckout}
              >
                <CreditCardIcon className="h-4 w-4" />
                {t("payNow")}
              </Button>
            ) : null}
            {isAdmin ? <IconMenuActions actions={getActions(invoice)} /> : null}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <dl className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">{t("reference")}</dt>
            <dd className="font-medium">{invoice.reference}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("invoiceDate")}</dt>
            <dd className="font-medium">{formatDate(invoice.invoice_date)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("dueDate")}</dt>
            <dd className="font-medium">
              {invoice.due_date ? formatDate(invoice.due_date) : "-"}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("amountExcTax")}</dt>
            <dd className="font-medium">
              {formatCurrency(invoice.amount_exc_tax, invoice.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("amountIncTax")}</dt>
            <dd className="font-medium">
              {formatCurrency(invoice.amount_inc_tax, invoice.currency)}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
